import { Component } from '@theme/component';

/**
 * Sol Instagram ribbon.
 *
 * The design draws the desktop run as a transform on a fixed-width track with
 * arrows, and the mobile run as a native scroll-snap scroller. Both are the
 * same thing, so this is one native scroller for every width: the arrows just
 * scroll it. That keeps the keyboard, the trackpad and the touch drag working
 * for free, leaves the tiles reachable with JS off, and means the step is read
 * off the real tile width rather than the design's hard-coded 324px.
 *
 * The tiles are either the theme's own `.sol-ig__tile` or Instafeed's
 * `.instafeed-new-layout-item`, which sol-blocks.css sizes identically. Both
 * are measured at click time, not on connect: Instafeed renders after
 * window.load, so on connect its track is still empty.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport - The scrolling element.
 *
 * @extends Component<Refs>
 */
class SolIgRibbon extends Component {
  requiredRefs = ['viewport'];

  /** One tile plus the gap, measured rather than assumed. */
  get #step() {
    const { viewport } = this.refs;
    const tile = viewport.querySelector('.sol-ig__tile, .instafeed-new-layout-item');

    if (!tile) return viewport.clientWidth;

    const gap = parseFloat(getComputedStyle(tile.parentElement).columnGap) || 0;

    return tile.getBoundingClientRect().width + gap;
  }

  /** Furthest scrollable offset, floored at 0 for a track that already fits. */
  get #max() {
    const { viewport } = this.refs;

    return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
  }

  next() {
    const { viewport } = this.refs;
    // Within a pixel of the end, the next press wraps to the start.
    const left = viewport.scrollLeft >= this.#max - 1 ? 0 : viewport.scrollLeft + this.#step;

    this.#scrollTo(left);
  }

  previous() {
    const { viewport } = this.refs;
    const left = viewport.scrollLeft <= 1 ? this.#max : viewport.scrollLeft - this.#step;

    this.#scrollTo(left);
  }

  /** @param {number} left */
  #scrollTo(left) {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.refs.viewport.scrollTo({ left, behavior: reduced ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('sol-ig-ribbon')) {
  customElements.define('sol-ig-ribbon', SolIgRibbon);
}
